import { useEffect, useMemo, useState } from "react";
import { Search, RefreshCw, Download, Users, CalendarDays, LogOut, Loader2 } from "lucide-react";

const STATUS_OPTIONS = ["new", "warm", "hot", "converted", "not interested"];
const STAGE_OPTIONS = ["follow up", "call scheduled", "session done", "enrolled", "closed"];

const STATUS_STYLE = {
  new: { bg: "#EEF1F4", color: "#5B6472" },
  warm: { bg: "#FEF3C7", color: "#92400E" },
  hot: { bg: "#FEE2E2", color: "#B91C1C" },
  converted: { bg: "#D1FAE5", color: "#047857" },
  "not interested": { bg: "#E5E7EB", color: "#6B7280" },
};

const api = (path, opts = {}) =>
  fetch(`/api${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}`,
      ...(opts.headers || {}),
    },
  });

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return "-";
  }
};

const inputCls =
  "w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#0ED9B5] focus:ring-2 focus:ring-[#0ED9B5]/20";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) {
        const d = data.detail;
        setError(typeof d === "string" ? d : "Login failed. Please try again.");
        return;
      }
      localStorage.setItem("admin_token", data.token);
      onLogin(data);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: "#F3F5F7" }} data-testid="admin-login">
      <div className="w-full max-w-sm bg-white rounded-2xl p-8" style={{ boxShadow: "0 8px 40px rgba(13,17,23,0.08)", border: "1px solid #E5E9EE" }}>
        <div className="w-14 h-14 rounded-xl overflow-hidden mb-5" style={{ border: "1px solid #E5E9EE" }}>
          <img src="/images/logo.png" alt="Institute logo" className="w-full h-full object-cover" data-testid="admin-login-logo" />
        </div>
        <h1 className="font-display text-2xl font-semibold uppercase text-gray-900">Leads & Follow-Ups</h1>
        <p className="mt-1 text-sm text-gray-500 mb-6">Admin sign in</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email</label>
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Password</label>
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
          </div>
          {error && <p className="text-sm" style={{ color: "#FF5C5C" }} data-testid="admin-login-error">{error}</p>}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-full py-3 text-sm font-bold flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
            style={{ background: "#0ED9B5", color: "#0D1117" }}
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
}

const thCls = "text-left text-[11px] font-bold uppercase tracking-wider text-gray-400 px-4 py-3 whitespace-nowrap";
const tdCls = "px-4 py-3.5 text-sm text-gray-700 whitespace-nowrap align-middle";

function StatusSelect({ value, onChange, testId }) {
  const s = STATUS_STYLE[value] || STATUS_STYLE.new;
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-full px-3 py-1.5 text-xs font-semibold border-0 cursor-pointer focus:outline-none capitalize"
      style={{ background: s.bg, color: s.color }}
    >
      {STATUS_OPTIONS.map((o) => (
        <option key={o} value={o} className="capitalize">{o}</option>
      ))}
    </select>
  );
}

function StageSelect({ value, onChange, testId }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 cursor-pointer focus:outline-none capitalize"
      style={{ background: "#F3F5F7", border: "1px solid #E5E9EE" }}
    >
      {STAGE_OPTIONS.map((o) => (
        <option key={o} value={o} className="capitalize">{o}</option>
      ))}
    </select>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [regs, setRegs] = useState([]);
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("");
  const [stageF, setStageF] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, r] = await Promise.all([api("/admin/leads"), api("/admin/registrations")]);
      if (l.status === 401 || r.status === 401) {
        onLogout();
        return;
      }
      setLeads(await l.json());
      setRegs(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rows = tab === "leads" ? leads : regs;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && ![r.name, r.phone, r.email].some((v) => (v || "").toLowerCase().includes(q))) return false;
      if (statusF && r.status !== statusF) return false;
      if (stageF && r.pipeline_stage !== stageF) return false;
      return true;
    });
  }, [rows, search, statusF, stageF]);

  const patch = async (id, updates) => {
    const setter = tab === "leads" ? setLeads : setRegs;
    setter((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)));
    await api(`/admin/${tab === "leads" ? "leads" : "registrations"}/${id}`, { method: "PATCH", body: JSON.stringify(updates) });
  };

  const exportCsv = () => {
    const isLeads = tab === "leads";
    const headers = isLeads
      ? ["Created", "Name", "Phone", "Email", "Course", "City", "Current Stage", "Best Time", "Status", "Pipeline Stage", "Notes"]
      : ["Created", "Name", "Phone", "Email", "Current Stage", "Status", "Pipeline Stage", "Notes"];
    const lines = filtered.map((r) =>
      isLeads
        ? [fmtDate(r.created_at), r.name, r.phone, r.email, r.interest, r.city, r.stage, r.best_time, r.status, r.pipeline_stage, r.notes]
        : [fmtDate(r.created_at), r.name, r.phone, r.email, r.stage, r.status, r.pipeline_stage, r.notes]
    );
    const csv = [headers, ...lines].map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv" }));
    a.download = `${tab}-export.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen" style={{ background: "#F3F5F7" }} data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold uppercase text-gray-900" data-testid="admin-title">Leads & Follow-Ups</h1>
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors"
          >
            <LogOut size={14} />
            Logout
          </button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex rounded-full p-1 gap-1" style={{ background: "#E9EDF1" }} data-testid="admin-tabs">
            {[
              { key: "leads", label: "Leads", icon: Users },
              { key: "seminar", label: "Seminar", icon: CalendarDays },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => { setTab(t.key); setSearch(""); setStatusF(""); setStageF(""); }}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors"
                style={tab === t.key ? { background: "#0ED9B5", color: "#0D1117" } : { color: "#5B6472" }}
              >
                <t.icon size={15} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, email"
              className={`${inputCls} !pl-9 !rounded-full`}
            />
          </div>
          <select data-testid="admin-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)} className={`${inputCls} !w-auto !rounded-full capitalize`}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((o) => <option key={o} value={o} className="capitalize">{o}</option>)}
          </select>
          <select data-testid="admin-stage-filter" value={stageF} onChange={(e) => setStageF(e.target.value)} className={`${inputCls} !w-auto !rounded-full capitalize`}>
            <option value="">All stages</option>
            {STAGE_OPTIONS.map((o) => <option key={o} value={o} className="capitalize">{o}</option>)}
          </select>
          <div className="flex gap-2.5 ml-auto">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-gray-600 bg-white transition-colors hover:text-gray-900"
              style={{ border: "1px solid #E5E9EE" }}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              data-testid="admin-export-button"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold"
              style={{ background: "#0ED9B5", color: "#0D1117" }}
            >
              <Download size={14} />
              Export Excel
            </button>
          </div>
        </div>

        <p className="mt-4 text-sm text-gray-500" data-testid="admin-count">
          Showing {filtered.length} of {rows.length} {tab === "leads" ? "leads" : "registrations"}
        </p>

        <div className="mt-3 bg-white rounded-2xl overflow-x-auto" style={{ border: "1px solid #E5E9EE" }} data-testid="admin-table-card">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr style={{ borderBottom: "1px solid #EEF1F4" }}>
                <th className={thCls}>Created</th>
                <th className={thCls}>Name</th>
                <th className={thCls}>Phone</th>
                <th className={thCls}>Email</th>
                {tab === "leads" ? (
                  <>
                    <th className={thCls}>Course</th>
                    <th className={thCls}>City</th>
                    <th className={thCls}>Current Stage</th>
                    <th className={thCls}>Best Time</th>
                  </>
                ) : (
                  <th className={thCls}>Current Stage</th>
                )}
                <th className={thCls}>Status</th>
                <th className={thCls}>Stage</th>
                <th className={thCls}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} style={{ borderBottom: "1px solid #F3F5F7" }} data-testid={`lead-row-${i}`}>
                  <td className={`${tdCls} text-gray-400 text-xs`}>{fmtDate(r.created_at)}</td>
                  <td className={`${tdCls} font-semibold text-gray-900`}>{r.name}</td>
                  <td className={tdCls}>{r.phone}</td>
                  <td className={tdCls}>{r.email}</td>
                  {tab === "leads" ? (
                    <>
                      <td className={tdCls}>{r.interest || "-"}</td>
                      <td className={tdCls}>{r.city || "-"}</td>
                      <td className={tdCls}>{r.stage || "-"}</td>
                      <td className={tdCls}>{r.best_time || "-"}</td>
                    </>
                  ) : (
                    <td className={tdCls}>{r.stage || "-"}</td>
                  )}
                  <td className={tdCls}>
                    <StatusSelect testId={`status-select-${i}`} value={r.status} onChange={(v) => patch(r.id, { status: v })} />
                  </td>
                  <td className={tdCls}>
                    <StageSelect testId={`stage-select-${i}`} value={r.pipeline_stage} onChange={(v) => patch(r.id, { pipeline_stage: v })} />
                  </td>
                  <td className={tdCls}>
                    <input
                      data-testid={`notes-input-${i}`}
                      defaultValue={r.notes}
                      placeholder="Add note..."
                      onBlur={(e) => e.target.value !== r.notes && patch(r.id, { notes: e.target.value })}
                      className="rounded-lg px-3 py-2 text-xs text-gray-600 w-44 focus:outline-none focus:border-[#0ED9B5]"
                      style={{ background: "#F9FAFB", border: "1px solid #E5E9EE" }}
                    />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-sm text-gray-400" data-testid="admin-empty">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {loading && (
            <div className="px-4 py-12 flex items-center justify-center gap-2 text-sm text-gray-400">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      setUser(null);
      return;
    }
    api("/auth/me")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setUser)
      .catch(() => {
        localStorage.removeItem("admin_token");
        setUser(null);
      });
  }, []);

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F3F5F7" }}>
        <Loader2 size={22} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (!user) return <Login onLogin={setUser} />;

  return (
    <Dashboard
      onLogout={() => {
        localStorage.removeItem("admin_token");
        setUser(null);
      }}
    />
  );
}
